import { startTransition, useCallback } from 'react';

import { useIntl } from 'react-intl';

import { remapVitalStatsEventForQuickFind } from '../../common/utils/events/remap-vital-stats-event-for-quick-find';
import { sha256Hash } from '../../common/utils/events/sha256-hash';
import { TASK_NAMES } from '../../common/utils/events/task-names';
import {
	onTaskAbort,
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../../common/utils/events/vital-stats-events';
import { serveJiraNINFpsNLQNavigation } from '../../common/utils/experiments/experiment-exports';
import { getWordCount } from '../../common/utils/quick-find/get-word-count';
import { usePrimaryProduct } from '../store/bootstrap';
import { useQuickFindAnalytics, useQuickFindAttributes } from './analytics';
import { useIntentDetectionVitalStatsTaskState } from './intent-detection-vitalstats-context';

export type AbortReason =
	| 'dialog_closed'
	| 'footer_link_selected'
	| 'search_submitted'
	| 'result_selected'
	| 'superseded_by_new_query'
	| 'text_cleared';

/**
 * Hook to manage VitalStats events for intent detection UX task lifecycle.
 * Tracks the time from when intent detection starts until the UI updates
 * (success), fails, or is aborted (dialog closed, search submitted,
 * result selected, text cleared, superseded by new query).
 *
 * Uses shared context to maintain state across multiple hook instances.
 * Gated behind the rovo_jira_intent_detection_audience_gate feature gate.
 */
export const useIntentDetectionVitalStats: () => {
	startTask: (query: string) => void;
	completeTaskSuccess: () => void;
	completeTaskFail: (error?: Error) => void;
	abortTask: (abortReason: AbortReason) => void;
	isTaskActive: () => boolean;
} = () => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	const intl = useIntl();
	const taskStateRef = useIntentDetectionVitalStatsTaskState();
	const [primaryProduct] = usePrimaryProduct();

	const intentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
		locale: intl.locale,
	});
	const isEnabled = intentDetectionExperiment?.cohort === 'test';
	const cohort = intentDetectionExperiment?.cohort;

	const startTask = useCallback(
		(query: string): void => {
			if (!isEnabled) {
				return;
			}

			const taskId = crypto.randomUUID();
			const searchSessionId = commonAttributes.searchSessionId || '';
			const queryHash = sha256Hash(query);
			const queryLength = query.length;
			const wordCount = getWordCount(query);

			// Set the shared context state
			if (taskStateRef.current) {
				taskStateRef.current.id = taskId;
				taskStateRef.current.state = 'started';
				taskStateRef.current.queryHash = queryHash;
				taskStateRef.current.queryLength = queryLength;
				taskStateRef.current.wordCount = wordCount;
			}

			fireAnalyticsEvent(
				remapVitalStatsEventForQuickFind(
					onTaskStart({
						searchSessionId,
						taskId,
						task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_UX,
						moreContext: { queryHash, queryLength, wordCount, cohort },
					}),
				),
			);
		},
		[isEnabled, cohort, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const completeTaskSuccess = useCallback((): void => {
		// Check if task is active using the shared context state
		if (!isEnabled || taskStateRef.current?.state !== 'started') {
			return;
		}

		const { id: taskId, queryHash, queryLength, wordCount } = taskStateRef.current;
		const searchSessionId = commonAttributes.searchSessionId || '';

		// taskId is guaranteed to be non-null when state is 'started'
		if (!taskId) {
			return;
		}

		fireAnalyticsEvent(
			remapVitalStatsEventForQuickFind(
				onTaskSuccess({
					searchSessionId,
					taskId,
					task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_UX,
					moreContext: { queryHash, queryLength, wordCount, cohort },
				}),
			),
		);

		// Mark task as completed
		taskStateRef.current.state = 'completed';
	}, [isEnabled, cohort, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef]);

	const completeTaskFail = useCallback(
		(error?: Error): void => {
			// Check if task is active using the shared context state
			if (!isEnabled || taskStateRef.current?.state !== 'started') {
				return;
			}

			const { id: taskId, queryHash, queryLength, wordCount } = taskStateRef.current;
			const searchSessionId = commonAttributes.searchSessionId || '';

			// taskId is guaranteed to be non-null when state is 'started'
			if (!taskId) {
				return;
			}

			fireAnalyticsEvent(
				remapVitalStatsEventForQuickFind(
					onTaskFail({
						searchSessionId,
						taskId,
						task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_UX,
						moreContext: {
							queryHash,
							queryLength,
							wordCount,
							cohort,
							errorName: error?.name || 'UnknownError',
							errorMessage: error?.message || 'An unknown error occurred',
						},
					}),
				),
			);

			// Mark task as failed
			taskStateRef.current.state = 'failed';
		},
		[isEnabled, cohort, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const abortTask = useCallback(
		(abortReason: AbortReason): void => {
			// Check if task is active using the shared context state
			if (!isEnabled || taskStateRef.current?.state !== 'started') {
				return;
			}

			const { id: taskId, queryHash, queryLength, wordCount } = taskStateRef.current;
			const searchSessionId = commonAttributes.searchSessionId || '';

			// taskId is guaranteed to be non-null when state is 'started'
			if (!taskId) {
				return;
			}

			// Mark task as aborted FIRST (synchronously) to prevent race conditions
			taskStateRef.current.state = 'aborted';

			// Then defer the analytics event to not block rendering
			startTransition(() => {
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskAbort({
							searchSessionId,
							taskId,
							task: TASK_NAMES.QUICK_FIND_INTENT_DETECTION_UX,
							moreContext: {
								abortReason,
								queryHash,
								queryLength,
								wordCount,
								cohort,
							},
						}),
					),
				);
			});
		},
		[isEnabled, cohort, commonAttributes.searchSessionId, fireAnalyticsEvent, taskStateRef],
	);

	const isTaskActive = useCallback((): boolean => {
		return taskStateRef.current?.state === 'started';
	}, [taskStateRef]);

	return {
		startTask,
		completeTaskSuccess,
		completeTaskFail,
		abortTask,
		isTaskActive,
	};
};
